import { FC } from 'react';
import { Game } from '../games.model';
import { GameGrid } from '../GameGrid';
import { TileRowCollection, SectionConfig, GridWidth } from '../GameCard/GameCard.types';
import { Grid, GridItem, GridColumnAmount } from '../../shared/Grid';
import { useBreakpointInfo, MediaBreakpoint } from '../../../hooks/useDeviceInfo';
import { joinStrings } from '../../../utils/string';

export type TileRowSectionMode = 'carousel' | 'view-all';

interface TileRowSectionProps {
  config: TileRowCollection;
  findGameById: (id: string) => Game | undefined;
  mode?: TileRowSectionMode;
  disabled?: boolean;
  showGameName?: boolean;
  fallbackImageAltText?: string;
  fallbackImageUrl?: string;
  onViewGameInfo?: (game: Game) => void;
  onPlayDemo?: (game: Game) => void;
  onPlayGame?: (game: Game) => void;
  containerElementWidth?: number;
}

const DEFAULT_GRID_ITEM_PROPS = {
  half: { startCol: 1 as GridColumnAmount, endCol: 4 as GridColumnAmount },
  full: { startCol: 1 as GridColumnAmount, endCol: 2 as GridColumnAmount },
} as const;

const VIEW_ALL_GRID_ITEM_PROPS = {
  half: {},
  full: { startCol: 1 as GridColumnAmount, endCol: 2 as GridColumnAmount },
} as const;

export const TileRowSection: FC<TileRowSectionProps> = ({
  config,
  findGameById,
  mode = 'carousel',
  disabled = false,
  showGameName = true,
  fallbackImageAltText = '',
  fallbackImageUrl = '',
  onViewGameInfo,
  onPlayDemo,
  onPlayGame,
  containerElementWidth = 0,
}) => {
  const shouldUseMobileLayout = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]);
  const layout: 'full' | 'half' = config.Width === GridWidth.FULL ? 'full' : 'half';

  const getGamesForSection = (sectionConfig: SectionConfig): Game[] => {
    if (!sectionConfig?.games) return [];

    return sectionConfig.games.map((id: string) => findGameById(id)).filter((game): game is Game => Boolean(game));
  };

  const renderSection = (sectionConfig: SectionConfig | undefined, key: string): JSX.Element | null => {
    if (!sectionConfig) {
      return null;
    }

    const sectionGames = getGamesForSection(sectionConfig);

    if (sectionGames.length === 0) {
      return null;
    }

    const effectiveContainerWidth =
      mode === 'view-all' && layout === 'half' && !shouldUseMobileLayout
        ? containerElementWidth / 2
        : containerElementWidth;

    return (
      <div key={key} className="carousel-grid-section">
        <GameGrid
          sectionType={sectionConfig.type}
          games={sectionGames}
          disabled={disabled}
          showGameName={showGameName}
          fallbackImageAltText={fallbackImageAltText}
          fallbackImageUrl={fallbackImageUrl}
          onViewGameInfo={onViewGameInfo}
          onPlayDemo={onPlayDemo}
          onPlayGame={onPlayGame}
          containerElementWidth={effectiveContainerWidth}
        />
      </div>
    );
  };

  type GridItemLayoutProps = Partial<
    Record<'full' | 'half', { startCol?: GridColumnAmount; endCol?: GridColumnAmount }>
  >;

  const sections = [
    {
      key: 'half-width',
      content: renderSection(config.half_width_tile, 'half-width'),
      showOn: ['half', 'full'] as Array<'half' | 'full'>,
      gridItemProps:
        mode === 'view-all'
          ? {
              half: {},
              full: VIEW_ALL_GRID_ITEM_PROPS.full,
            }
          : DEFAULT_GRID_ITEM_PROPS,
    },
    {
      key: 'first-full',
      content: renderSection(config.first_full_width_tile, 'first'),
      showOn: ['full', 'half'] as Array<'half' | 'full'>,
      gridItemProps:
        mode === 'view-all'
          ? {
              half: {},
            }
          : {
              half: DEFAULT_GRID_ITEM_PROPS.half,
            },
    },
    {
      key: 'second-full',
      content: renderSection(config.second_full_width_tile, 'second'),
      showOn: ['full', 'half'] as Array<'half' | 'full'>,
      gridItemProps:
        mode === 'view-all'
          ? {
              half: {},
            }
          : {
              half: DEFAULT_GRID_ITEM_PROPS.half,
            },
    },
    {
      key: 'third-full',
      content: renderSection(config.third_full_width_tile_block, 'third'),
      showOn: ['full', 'half'] as Array<'half' | 'full'>,
      gridItemProps:
        mode === 'view-all'
          ? {
              half: {},
            }
          : {
              half: DEFAULT_GRID_ITEM_PROPS.half,
            },
    },
  ];

  const sectionsToRender = sections.filter((section) => section.showOn.includes(layout) && section.content);
  const renderedSectionCount = sectionsToRender.length;
  const isMergedHalfWidth = mode === 'view-all' && Boolean(config.id?.startsWith('view-all-merged-'));

  const isViewAll = mode === 'view-all';
  const isDesktop = !shouldUseMobileLayout;
  const isHalfLayout = layout === 'half';
  const isFullLayout = layout === 'full';
  const hasMultipleMergedSections = isMergedHalfWidth && renderedSectionCount > 1;
  const shouldApplyMergedColumns = isViewAll && isDesktop && isFullLayout && hasMultipleMergedSections;

  let columnsValue = (shouldUseMobileLayout ? 1 : 3) as GridColumnAmount;

  if (isViewAll && isDesktop && isHalfLayout) {
    columnsValue = 2 as GridColumnAmount;
  } else if (shouldApplyMergedColumns) {
    columnsValue = Math.min(Math.max(renderedSectionCount, 1), 2) as GridColumnAmount;
  }

  const gap = shouldUseMobileLayout ? 's' : 'm';

  const baseLayoutClass = isFullLayout
    ? 'carousel-grid--full-width'
    : isViewAll
      ? 'carousel-grid--view-all-half'
      : 'carousel-grid--half-width';

  const gridClassName = joinStrings([
    'carousel-grid',
    baseLayoutClass,
    shouldApplyMergedColumns ? 'carousel-grid--merged-half' : undefined,
  ]);

  if (renderedSectionCount === 0) {
    return null;
  }

  return (
    <div className="carousel-tile-row">
      <div className="carousel-tile-row__content">
        <Grid columns={columnsValue} gap={gap} className={gridClassName}>
          {sectionsToRender.map((section) => {
            const itemProps = (section.gridItemProps as GridItemLayoutProps)?.[layout] ?? {};

            return (
              <GridItem key={section.key} {...itemProps}>
                {section.content}
              </GridItem>
            );
          })}
        </Grid>
      </div>
    </div>
  );
};
